import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import de from './locales/de.json';
import pt from './locales/pt.json';
import ar from './locales/ar.json';
import ja from './locales/ja.json';
import hi from './locales/hi.json';
import zh from './locales/zh.json';
import he from './locales/he.json';

import { withTranslationAliases } from './withTranslationAliases';

const resources = {
  en: { translation: withTranslationAliases(en, 'en') },
  es: { translation: withTranslationAliases(es, 'es') },
  fr: { translation: withTranslationAliases(fr, 'fr') },
  de: { translation: withTranslationAliases(de, 'de') },
  pt: { translation: withTranslationAliases(pt, 'pt') },
  ar: { translation: withTranslationAliases(ar, 'ar') },
  ja: { translation: withTranslationAliases(ja, 'ja') },
  hi: { translation: withTranslationAliases(hi, 'hi') },
  zh: { translation: withTranslationAliases(zh, 'zh') },
  he: { translation: withTranslationAliases(he, 'he') },
};

// Map country codes to supported languages
const countryToLanguage: Record<string, string> = {
  // Spanish
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es', EC: 'es', GT: 'es', CU: 'es', BO: 'es', DO: 'es', HN: 'es', PY: 'es', SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es',
  // French
  FR: 'fr', CA: 'fr', BE: 'fr', CH: 'fr', SN: 'fr', CI: 'fr', ML: 'fr', CM: 'fr', MG: 'fr', NE: 'fr', BF: 'fr', TG: 'fr', BJ: 'fr', GA: 'fr', CG: 'fr', TD: 'fr', CF: 'fr', DJ: 'fr', KM: 'fr', RE: 'fr', GP: 'fr', MQ: 'fr', GF: 'fr', NC: 'fr', PF: 'fr', WF: 'fr', MC: 'fr', LU: 'fr',
  // German
  DE: 'de', AT: 'de', LI: 'de',
  // Portuguese
  PT: 'pt', BR: 'pt', AO: 'pt', MZ: 'pt', GW: 'pt', TL: 'pt', CV: 'pt', ST: 'pt',
  // Arabic
  SA: 'ar', EG: 'ar', AE: 'ar', IQ: 'ar', MA: 'ar', DZ: 'ar', SD: 'ar', YE: 'ar', SY: 'ar', TN: 'ar', JO: 'ar', LY: 'ar', LB: 'ar', OM: 'ar', KW: 'ar', QA: 'ar', BH: 'ar', MR: 'ar', PS: 'ar',
  // Japanese
  JP: 'ja',
  // Hindi
  IN: 'hi',
  // Chinese
  CN: 'zh', TW: 'zh', HK: 'zh', MO: 'zh', SG: 'zh',
  // Hebrew
  IL: 'he',
  // English (default for these + fallback)
  US: 'en', GB: 'en', AU: 'en', NZ: 'en', IE: 'en', ZA: 'en', NG: 'en', KE: 'en', GH: 'en', PH: 'en', PK: 'en',
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: localStorage.getItem('i18nextLng') || 'en',
    fallbackLng: 'en',
    supportedLngs: ['en', 'es', 'fr', 'de', 'pt', 'ar', 'ja', 'hi', 'zh', 'he'],
    interpolation: {
      escapeValue: false,
    },
  });

// Detect language by IP location (only if not already set by user)
const detectLanguageByIP = async () => {
  const storedLang = localStorage.getItem('i18nextLng');
  if (storedLang) return; // User already chose a language

  try {
    const response = await fetch('https://ipapi.co/json/', { 
      signal: AbortSignal.timeout(3000) 
    });
    const data = await response.json();
    const countryCode = data.country_code;
    const detectedLang = countryToLanguage[countryCode] || 'en';
    
    if (detectedLang !== i18n.language) {
      i18n.changeLanguage(detectedLang);
      localStorage.setItem('i18nextLng', detectedLang);
    }
  } catch (error) {
    console.log('IP detection failed, using default language');
  }
};

detectLanguageByIP();

// Handle RTL for Arabic and Hebrew
i18n.on('languageChanged', (lng) => {
  const isRTL = lng === 'ar' || lng === 'he';
  document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  document.documentElement.lang = lng;
});

export default i18n;
